// The `{Slicer.` menu's one hook. It owns the menu state, reads the board's slicers and
// their live values, and returns stable functions. A field adapter (the antd Input in
// `SlicerTokenInput`, the TipTap editor in `useTiptapSlicerMenu`) only translates events
// into `refresh`, `onKeyDown`, `pick`, `insert` and `close`; every rule lives in the pure
// modules `Utils/slicerTokenTrigger.mjs` and `Utils/slicerTokenMenuModel.mjs`.

import { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useControllers, useSlicerVariables } from "../../Contexts/dashboardHooks";
import { sliceColor } from "../../Processing/controllerBindings";
import type { ControllerKind } from "../../Processing/types";
import type { SlicerKind } from "../../Utils/resolveTemplate";
import { buildMenuModel, slicerMenuRows } from "../../Utils/slicerTokenMenuModel.mjs";
import {
  CLOSED_MENU,
  dismissMenu,
  menuKey,
  menuStep,
  moveActive,
  parseSlicerTrigger,
  slicerTokenCaret,
} from "../../Utils/slicerTokenTrigger.mjs";

export interface CaretText {
  /** The text from the start of the field or textblock to the caret. */
  before: string;
  /** The text from the caret to the end of the field or textblock. */
  after: string;
  /** The absolute position of the first character of `before`. */
  base: number;
}

/** What a field gives the hook. Positions are absolute: `base + offset`. */
export interface SlicerTokenAdapter {
  /** Null for a range selection, or when the caret is not inside text. */
  caretText(): CaretText | null;
  selection(): { from: number; to: number } | null;
  /** Replace `[from, to)` with `text` and put the caret `caretOffset` characters after `from`. */
  replace(from: number, to: number, text: string, caretOffset?: number): void;
  caretRect?(): DOMRect | null;
}

export interface SlicerMenuVariable {
  name: string;
  token: string;
  meaning: string;
  text: string;
  blank: boolean;
}

export interface SlicerMenuRow {
  key: string;
  label: string;
  color: string;
  controls: ControllerKind[];
  kindWord: SlicerKind;
  kindLabel: string;
  variables: SlicerMenuVariable[];
}

export interface SlicerMenuHeader {
  key: string;
  label: string;
  color: string;
  kindLabel: string;
}

export type SlicerMenuItem =
  | { kind: "slicer"; id: string; key: string; label: string; color: string; kindLabel: string; text: string }
  | { kind: "variable"; id: string; name: string; valueText: string; blank: boolean; text: string; formatText: string }
  | { kind: "empty"; text: string; detail: string | null };

export interface SlicerMenuModel {
  level: 1 | 2;
  header: SlicerMenuHeader | null;
  items: SlicerMenuItem[];
  pickable: number;
  footer: { text: string; example: string; tail: string } | null;
}

export type SlicerMenuState =
  | { open: false; dismissed: { brace: number; text: string } | null }
  | {
      open: true;
      level: 1 | 2;
      key: string | null;
      partial: string;
      /** Absolute position of the opening brace. */
      brace: number;
      /** Absolute position of the caret. */
      end: number;
      text: string;
      activeIndex: number;
      dismissed: null;
    };

/** The key fields the menu reads; a native or a React keyboard event satisfies it. */
export interface MenuKeyEvent {
  key: string;
  shiftKey: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  isComposing?: boolean;
}

type MenuAction =
  | { type: "move"; delta: number }
  | { type: "pick"; formatSlot: boolean }
  | { type: "close"; handled: boolean };

export interface SlicerTokenTrigger {
  menu: SlicerMenuState;
  model: SlicerMenuModel | null;
  rows: SlicerMenuRow[];
  /** The `id` of the listbox, for `aria-controls`. */
  listId: string;
  /** Re-parse the field; the adapters call it on every change and caret move. */
  refresh(): void;
  /** True when the menu handled the key; the adapter then prevents the default and stops propagation. */
  onKeyDown(event: MenuKeyEvent): boolean;
  /** Pick a row (the active one by default), with the format slot at level 2 when asked. */
  pick(index?: number, formatSlot?: boolean): void;
  /** Insert complete token text over the current selection, as the help popover does. */
  insert(text: string): boolean;
  /** Close the menu; it stays closed while the typed partial is unchanged. */
  close(): void;
  setActive(index: number): void;
}

/** The id of one option row, for `aria-activedescendant`. */
export function optionId(listId: string, index: number): string {
  return `${listId}-option-${index}`;
}

/** The menu rows for the board's slicers, which the menu and the help popover share. */
export function useSlicerMenuRows(): SlicerMenuRow[] {
  const controllers = useControllers();
  const values = useSlicerVariables();
  return useMemo(
    () =>
      slicerMenuRows(
        controllers.map((c) => ({
          key: c.key,
          label: c.label,
          controls: c.controls,
          color: sliceColor(c.spec.slicer_builder_misc),
        })),
        values,
      ) as SlicerMenuRow[],
    [controllers, values],
  );
}

export function useSlicerTokenTrigger(adapter: SlicerTokenAdapter): SlicerTokenTrigger {
  const rows = useSlicerMenuRows();
  const listId = useId();
  const [menu, setMenu] = useState<SlicerMenuState>(CLOSED_MENU as SlicerMenuState);
  const model = useMemo<SlicerMenuModel | null>(
    () => (menu.open ? (buildMenuModel(menu, rows) as SlicerMenuModel) : null),
    [menu, rows],
  );

  // The returned functions are stable and read the latest state through refs.
  const adapterRef = useRef(adapter);
  const menuRef = useRef(menu);
  const modelRef = useRef(model);
  useLayoutEffect(() => {
    adapterRef.current = adapter;
    menuRef.current = menu;
    modelRef.current = model;
  });

  const refresh = useCallback(() => {
    const caret = adapterRef.current.caretText();
    const trigger = caret ? parseSlicerTrigger(caret.before, caret.after) : null;
    setMenu((prev) => menuStep(prev, trigger, caret) as SlicerMenuState);
  }, []);

  const close = useCallback(() => {
    setMenu((prev) => dismissMenu(prev) as SlicerMenuState);
  }, []);

  const setActive = useCallback((index: number) => {
    setMenu((prev) => (prev.open && prev.activeIndex !== index ? { ...prev, activeIndex: index } : prev));
  }, []);

  const pick = useCallback((index?: number, formatSlot = false) => {
    const state = menuRef.current;
    const current = modelRef.current;
    if (!state.open || !current) return;
    const item = current.items[index ?? state.activeIndex];
    if (!item || item.kind === "empty") return;
    const text = item.kind === "variable" && formatSlot ? item.formatText : item.text;
    const caretOffset = item.kind === "variable" ? slicerTokenCaret(text, formatSlot) : text.length;
    adapterRef.current.replace(state.brace, state.end, text, caretOffset);
  }, []);

  const insert = useCallback((text: string) => {
    const selection = adapterRef.current.selection();
    if (!selection) return false;
    adapterRef.current.replace(selection.from, selection.to, text, text.length);
    return true;
  }, []);

  const onKeyDown = useCallback(
    (event: MenuKeyEvent): boolean => {
      const action = menuKey(menuRef.current, event, modelRef.current?.pickable ?? 0) as MenuAction | null;
      if (!action) return false;
      if (action.type === "move") {
        setMenu((prev) =>
          prev.open
            ? { ...prev, activeIndex: moveActive(prev.activeIndex, action.delta, modelRef.current?.pickable ?? 0) }
            : prev,
        );
        return true;
      }
      if (action.type === "pick") {
        pick(undefined, action.formatSlot);
        return true;
      }
      close();
      return action.handled;
    },
    [pick, close],
  );

  return { menu, model, rows, listId, refresh, onKeyDown, pick, insert, close, setActive };
}
